import { useEffect, useRef, useState } from "react";
import { useTheme as styledTheme } from "styled-components";
import { useRippleEffect } from "../../hooks/useRippleEffect";
import { Squarebtn } from "../../Components/common/squareBtnProps";
import { RequestOTPButtonProps } from "../../Components/common/types";
import { baseURL, forgotPassword, verifyEmail } from "../../config/variables";

const RequestOTPButton = <T,>({
  email,
  purpose,
  showAlert,
  closeAlert,
  handleChange,
  validateFieldWrapper,
  errors,
}: RequestOTPButtonProps<T>) => {
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const styledComponentTheme = styledTheme();
  const otpButtonRef = useRef<HTMLButtonElement>(null);
  useRippleEffect(otpButtonRef);

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone; // Get user's time zone

  const handleResetRequestOTP = async () => {
    if (!email) {
      showAlert(true, "Error", "Please provide email", false);
      return;
    }
    // Simulate user typing in email field (triggers validation)
      handleChange({
        target: { name: "email", value: email },
      } as React.ChangeEvent<HTMLInputElement>);

    // Validate email before proceeding
    validateFieldWrapper("email" as keyof T, email ?? "");

    if (errors.email) {
      showAlert(true, "Error", errors.email, false);
      return;
    }

    setLoading(true);

    try {
      const endpoint = purpose === "password_reset"? baseURL + forgotPassword : baseURL + verifyEmail ;
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, timeZone, purpose }),
      });

      const data = await response.json();

      if (!response.ok) {
        showAlert(true, "Error", data.message, false);
        return;
      }

      showAlert(false, "Success", data.message, false, undefined, undefined, false);
      setTimeout(() => {
        closeAlert(); //close alert after 3 sec
      }, 3000);

      setTimer(60); // Start countdown
    } catch (error: any) {
      showAlert(true, "Error", error.message, false);
    } finally {
      setLoading(false);
    }
  };

  // Countdown effect
  useEffect(() => {
    if (timer > 0) {
      const countdown = setTimeout(() => setTimer(timer - 1), 1000);
      return () => clearTimeout(countdown);
    }
  }, [timer]);

  return (
    <Squarebtn
      onClick={ handleResetRequestOTP}
      disabled={loading || timer > 0}
      ref={otpButtonRef}
      type="button"
      height="54px"
      width="48%"
      $bacgrndColor={styledComponentTheme.text}
      $color={styledComponentTheme.circularIconTextBg}
    >
      {loading ? (
        <span
          className="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        ></span>
      ) : timer > 0 ? (
        `Resend after ${timer}s`
      ) : (
        "Request OTP"
      )}
    </Squarebtn>
  );
};

export default RequestOTPButton;
